import { useState } from "react";
import { Clipboard, GraduationCap } from "lucide-react";
import type { ResultLookupResponse, StudentResult, SubjectResult } from "../../shared/types";

type StudentInfo = Partial<StudentResult["student_info"]>;

type Props = {
  data: ResultLookupResponse;
};

const ui = {
  result: "\u0627\u0644\u0646\u062a\u064a\u062c\u0629",
  motherName: "\u0627\u0633\u0645 \u0627\u0644\u0623\u0645:",
  notMentioned: "\u063a\u064a\u0631 \u0645\u0630\u0643\u0648\u0631",
  total: "\u0627\u0644\u0645\u062c\u0645\u0648\u0639",
  copyResult: "\u0646\u0633\u062e \u0627\u0644\u0646\u062a\u064a\u062c\u0629",
  copied: "\u062a\u0645 \u0627\u0644\u0646\u0633\u062e",
  subject: "\u0627\u0644\u0645\u0627\u062f\u0629",
  mark: "\u0627\u0644\u0639\u0644\u0627\u0645\u0629",
  maxMark: "\u0627\u0644\u0639\u0644\u0627\u0645\u0629 \u0627\u0644\u0639\u0638\u0645\u0649",
  status: "\u0627\u0644\u062d\u0627\u0644\u0629",
  studentName: "\u0627\u0633\u0645 \u0627\u0644\u0637\u0627\u0644\u0628",
  certificateName: "\u0627\u0644\u0634\u0647\u0627\u062f\u0629",
  studentInfo: "\u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0637\u0627\u0644\u0628",
  marks: "\u0627\u0644\u0639\u0644\u0627\u0645\u0627\u062a",
  passed: "\u0646\u0627\u062c\u062d",
  failed: "\u0631\u0627\u0633\u0628",
  excluded: "\u062e\u0627\u0631\u062c \u0627\u0644\u0645\u062c\u0645\u0648\u0639",
  congrats: "\u0645\u0628\u0631\u0648\u0643\u060c \u0627\u0644\u0646\u062a\u064a\u062c\u0629 \u0646\u0627\u062c\u062d",
  unknownStudent: "\u0627\u0644\u0637\u0627\u0644\u0628",
  notAvailable: "-",
  unpublishedTitle: "\u0627\u0644\u0646\u062a\u064a\u062c\u0629 \u063a\u064a\u0631 \u0645\u062a\u0648\u0641\u0631\u0629 \u0628\u0639\u062f",
  unpublishedHelp: "\u0644\u0645 \u062a\u0635\u0644 \u0627\u0644\u0639\u0644\u0627\u0645\u0627\u062a \u0645\u0646 \u0627\u0644\u062c\u0647\u0629 \u0627\u0644\u0631\u0633\u0645\u064a\u0629 \u0644\u0647\u0630\u0647 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a. \u062d\u0627\u0648\u0644 \u0644\u0627\u062d\u0642\u0627.",
  attribution: "\u062a\u0645 \u0627\u0644\u0646\u0633\u062e \u0639\u0646 \u0637\u0631\u064a\u0642 \u0645\u0648\u0642\u0639 results.abdulhafizfaraj.dev",
};

export const ResultView = ({ data }: Props) => {
  const [copied, setCopied] = useState(false);
  const result = (data.result ?? {}) as Partial<StudentResult>;
  const studentInfo: StudentInfo = result.student_info ?? {};
  const resultText = typeof result.result === "string" ? result.result : "";
  const total = asNumber(result.total);
  const maxTotal = asNumber(result.max_total);
  const percentage = maxTotal !== undefined && maxTotal > 0 && total !== undefined ? Math.round((total / maxTotal) * 1000) / 10 : undefined;
  const isPassed = isPassedResult(resultText);
  const orderedSubjects = orderSubjects(Array.isArray(result.subject_info) ? result.subject_info : []);
  const completeTotals = total !== undefined && maxTotal !== undefined;
  const hasMarks = orderedSubjects.length > 0 && completeTotals;

  const copyResult = async () => {
    await navigator.clipboard.writeText(buildResultText(result, orderedSubjects));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <section className="rounded-md border border-white/10 bg-zinc-900 p-3 shadow-2xl shadow-black/20 sm:p-5" dir="rtl" aria-live="polite">
      {isPassed ? <SuccessFireworks /> : null}

      <div className="flex flex-col gap-3 border-b border-white/10 pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-sm bg-teal-400 px-2.5 py-1 text-xs font-bold text-zinc-950 sm:text-sm">
            <GraduationCap className="h-4 w-4" aria-hidden="true" />
            <span>{resultText || ui.result}</span>
          </div>
          <div>
            <h2 className="text-xl font-bold leading-7 text-white sm:text-2xl">{studentInfo.student_name || ui.unknownStudent}</h2>
            <p className="mt-0.5 text-xs text-zinc-400 sm:text-sm">
              {ui.motherName} {studentInfo.student_mother_name || ui.notMentioned}
            </p>
            {studentInfo.certificate_name ? <p className="mt-0.5 text-xs text-zinc-500 sm:text-sm">{studentInfo.certificate_name}</p> : null}
          </div>
        </div>
        <div className="flex items-stretch gap-2 sm:items-center">
          <button
            type="button"
            onClick={copyResult}
            className={`inline-flex min-w-28 items-center justify-center gap-2 rounded-sm border px-3 text-xs font-bold shadow-sm transition active:translate-y-px focus:outline-none focus:ring-2 focus:ring-teal-300 focus:ring-offset-2 focus:ring-offset-zinc-950 sm:h-10 ${copied ? "border-teal-400 bg-teal-400 text-zinc-950" : "border-teal-400 bg-zinc-950 text-teal-300 hover:bg-zinc-800 hover:text-teal-200 hover:shadow-md"}`}
            title={ui.copyResult}
            aria-label={ui.copyResult}
          >
            <Clipboard className="h-4 w-4" aria-hidden="true" />
            <span>{copied ? ui.copied : ui.copyResult}</span>
          </button>
          <div className="min-w-24 rounded-sm border border-zinc-700 bg-zinc-950 px-3 py-2 text-center">
            <p className="text-xs text-zinc-400">{ui.total}</p>
            <p className="mt-0.5 text-xl font-bold text-white sm:text-2xl">
              {formatTotal(total, maxTotal)}
            </p>
            {percentage !== undefined ? <p className="mt-0.5 text-xs font-semibold text-teal-300">{percentage}%</p> : null}
          </div>
        </div>
      </div>

      {hasMarks && completeTotals ? (
        <ResultTable subjects={orderedSubjects} total={total} maxTotal={maxTotal} resultText={resultText} isPassed={isPassed} />
      ) : (
        <UnavailablePanel message={result.message} />
      )}
    </section>
  );
};

type ResultTableProps = {
  subjects: SubjectResult[];
  total: number;
  maxTotal: number;
  resultText: string;
  isPassed: boolean;
};

const ResultTable = ({ subjects, total, maxTotal, resultText, isPassed }: ResultTableProps) => (
  <div className="mt-3 overflow-x-auto">
    <table className="min-w-full border-collapse text-right text-xs sm:text-sm">
      <thead>
        <tr className="border-b border-zinc-700 bg-zinc-950 text-zinc-300">
          <th className="px-2 py-2 font-bold">{ui.subject}</th>
          <th className="w-20 px-2 py-2 font-bold">{ui.mark}</th>
          <th className="w-24 px-2 py-2 font-bold">{ui.maxMark}</th>
          <th className="w-24 px-2 py-2 font-bold">{ui.status}</th>
        </tr>
      </thead>
      <tbody>
        {subjects.map((subject) => {
          const failed = isFailedSubject(subject);
          return (
            <tr key={subject.subject_name} className={`border-b last:border-0 ${failed ? "border-red-400/20 bg-red-950/40 text-red-100" : "border-zinc-800 text-zinc-200"}`}>
              <td className="px-2 py-2">
                <p className="font-semibold leading-5">{subject.subject_name}</p>
              </td>
              <td className={`px-2 py-2 font-bold ${failed ? "text-red-300" : "text-white"}`}>{subject.exam_mark}</td>
              <td className="px-2 py-2">{subject.max_mark ?? ui.notAvailable}</td>
              <td className="px-2 py-2">
                <span className={`inline-flex rounded-sm px-1.5 py-0.5 text-[11px] font-bold sm:text-xs ${failed ? "bg-red-400/20 text-red-200" : "bg-teal-400/15 text-teal-200"}`}>
                  {failed ? ui.failed : ui.passed}
                </span>
                {subject.excluded_from_total ? <p className="mt-0.5 text-[11px] text-zinc-500">{ui.excluded}</p> : null}
              </td>
            </tr>
          );
        })}
        <tr className="border-t-2 border-zinc-600 bg-zinc-800 text-white">
          <td className="px-2 py-2 font-bold">{ui.total}</td>
          <td className="px-2 py-2 font-bold">{total}</td>
          <td className="px-2 py-2 font-semibold">{maxTotal}</td>
          <td className="px-2 py-2">
            <span className={`inline-flex rounded-sm px-1.5 py-0.5 text-[11px] font-bold sm:text-xs ${isPassed ? "bg-teal-400/15 text-teal-200" : "bg-red-400/20 text-red-200"}`}>
              {resultText || ui.result}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

const UnavailablePanel = ({ message }: { message?: string }) => (
  <div className="mt-3 rounded-md border border-amber-400/30 bg-amber-950/35 p-4 text-sm leading-7 text-amber-100">
    <p className="font-bold">{ui.unpublishedTitle}</p>
    <p className="mt-1">{message || ui.unpublishedHelp}</p>
  </div>
);

const buildResultText = (result: Partial<StudentResult>, subjects: SubjectResult[]): string => {
  const studentInfo: StudentInfo = result.student_info ?? {};
  const resultText = typeof result.result === "string" ? result.result : "";
  const total = asNumber(result.total);
  const maxTotal = asNumber(result.max_total);
  const rows = subjects.map((subject) => {
    const failed = isFailedSubject(subject);
    const notes = [failed ? ui.failed : ui.passed, subject.excluded_from_total ? ui.excluded : ""].filter(Boolean).join(" - ");
    return [subject.subject_name, subject.exam_mark, subject.max_mark ?? ui.notAvailable, notes].join(" | ");
  });

  const lines = [
    ui.studentInfo,
    `${ui.result}: ${resultText || ui.notAvailable}`,
    `${ui.studentName}: ${studentInfo.student_name || ui.notAvailable}`,
    `${ui.motherName} ${studentInfo.student_mother_name || ui.notMentioned}`,
    `${ui.certificateName}: ${studentInfo.certificate_name || ui.notAvailable}`,
    "",
    ui.marks,
    ...(rows.length > 0 ? [[ui.subject, ui.mark, ui.maxMark, ui.status].join(" | "), ...rows] : [result.message || ui.unpublishedHelp]),
    total !== undefined || maxTotal !== undefined ? [ui.total, total ?? ui.notAvailable, maxTotal ?? ui.notAvailable, resultText || ui.notAvailable].join(" | ") : "",
    "",
    ui.attribution,
  ];

  return lines.filter((line, index) => line !== "" || lines[index - 1] !== "").join("\n");
};

const orderSubjects = (subjects: SubjectResult[]): SubjectResult[] => {
  return [...subjects].sort((a, b) => Number(Boolean(a.excluded_from_total)) - Number(Boolean(b.excluded_from_total)));
};

const isFailedSubject = (subject: SubjectResult): boolean => {
  if (typeof subject.is_fail === "boolean") {
    return subject.is_fail;
  }
  if (typeof subject.min_mark === "number") {
    return subject.exam_mark < subject.min_mark;
  }
  return false;
};

const isPassedResult = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized.includes(ui.failed) || normalized.includes("\u063a\u064a\u0631")) {
    return false;
  }
  return normalized === ui.passed || normalized === "pass" || normalized === "passed";
};

const asNumber = (value: unknown): number | undefined => {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};

const formatTotal = (total: number | undefined, maxTotal: number | undefined): string => {
  if (total !== undefined && maxTotal !== undefined) {
    return `${total}/${maxTotal}`;
  }
  if (total !== undefined) {
    return String(total);
  }
  return ui.notAvailable;
};

const SuccessFireworks = () => (
  <>
    <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-teal-400/30 bg-teal-950/35 px-3 py-2 text-teal-100">
      <p className="text-xs font-bold sm:text-sm">{ui.congrats}</p>
    </div>
    <div className="fireworks" aria-hidden="true">
      {Array.from({ length: 9 }, (_, burstIndex) => (
        <span className="firework-burst" key={burstIndex}>
          {Array.from({ length: 32 }, (_, particleIndex) => (
            <span className="firework-particle" key={particleIndex} />
          ))}
        </span>
      ))}
    </div>
  </>
);
